import styled, { css } from 'styled-components';
import { transparentize } from 'polished';

import {
  IMarginProps,
  marginPropsStyles,
} from '~/shared/interfaces/IMarginProps';
import { TSelectPosition, TSelectSize } from './types';

interface IInputWrapperProps {
  hasError: boolean;
  size: TSelectSize;
  isDisabled: boolean;
}

interface IOptionProps {
  size: TSelectSize;
  isActive?: boolean;
  isPlaceholder?: boolean;
  isDisabled?: boolean;
  level?: number;
}

interface IIconWrapperProps {
  size: TSelectSize;
  isDropdownVisible: boolean;
}

interface INoOptionsProps {
  size: TSelectSize;
}

interface IValueProps {
  size: TSelectSize;
  isVisible: boolean;
  isDisabled: boolean;
}

export interface IDropdownContainerProps {
  isVisible: boolean;
  selectClientRect: DOMRect;
}
interface IDropdownProps {
  isVisible: boolean;
  selectClientRect: DOMRect;
  position: TSelectPosition;
  size: TSelectSize;
}

export const Container = styled.label<IMarginProps>`
  position: relative;

  ${marginPropsStyles}
`;

const getHeight: Record<TSelectSize, string> = {
  lg: '48px',
  md: '42px',
  sm: '36px',
};

export const InputWrapper = styled.div<IInputWrapperProps>`
  position: relative;

  display: flex;
  align-items: center;

  height: ${({ size }) => getHeight[size]};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme, isDisabled }) =>
    isDisabled ? theme.colors.gray[50] : theme.colors.surface};
  border: ${({ theme }) => `1px solid ${theme.colors.gray[100]}`};
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};

  transition: all ease-in 0.2s;

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  ${({ theme, hasError }) =>
    hasError &&
    css`
      border-color: ${theme.colors.danger} !important;

      svg {
        color: ${theme.colors.danger} !important;
      }
    `}

  > input {
    background: none;
    height: 100%;
    overflow: hidden;
    flex: 1;
    padding-left: ${({ theme, size }) =>
      size === 'lg' ? theme.spacing.sm : '12px'};
    font-size: ${({ size }) => (size === 'sm' ? '12px' : '14px')};
    line-height: ${({ size }) => getHeight[size]};
    color: ${({ theme }) => theme.colors.onSurface};
    ${({ isDisabled }) =>
      isDisabled
        ? css`
            cursor: not-allowed;
          `
        : css`
            cursor: pointer;
          `}

    &::placeholder {
      color: ${({ theme }) => theme.colors.gray[200]};
    }
  }
`;

export const Value = styled.span<IValueProps>`
  position: absolute;
  top: 0;
  left: 0;
  height: ${({ size }) => `calc(${getHeight[size]} - 2px)`};
  padding: ${({ theme, size }) =>
    `0 ${size === 'lg' ? theme.spacing.sm : '12px'}`};
  font-size: ${({ size }) => (size === 'sm' ? '12px' : '14px')};
  line-height: ${({ size }) => `calc(${getHeight[size]} - 2px)`};
  color: ${({ theme }) => theme.colors.onSurface};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  display: inline-flex;
  align-items: center;
  z-index: 10;

  width: ${({ size, theme }) =>
    `calc(100% - ${
      size === 'sm' ? '0px' : size === 'lg' ? theme.spacing.sm : '12px'
    } - ${size === 'lg' ? theme.spacing.sm : '12px'} - 18px)`};
  background: ${({ theme, isDisabled }) =>
    isDisabled ? theme.colors.gray[50] : theme.colors.surface};

  opacity: 1;
  visibility: visible;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  transition: opacity ease-in 0.2s, visibility ease-in 0.2s;

  ${({ isVisible }) =>
    !isVisible &&
    css`
      opacity: 0;
      visibility: hidden;
    `}
`;

export const IconWrapper = styled.div<IIconWrapperProps>`
  display: flex;
  align-items: center;
  justify-content: center;

  height: 100%;
  padding: ${({ theme, size }) =>
    `0 ${size === 'lg' ? theme.spacing.sm : '12px'}`};

  transition: transform ease-in 0.3s;

  ${({ isDropdownVisible }) =>
    isDropdownVisible &&
    css`
      transform: rotate(180deg);
    `}

  > svg {
    width: ${({ size }) => (size === 'sm' ? '14px' : '18px')};
    height: ${({ size }) => (size === 'sm' ? '14px' : '18px')};
    color: ${({ theme }) => theme.colors.onSurface};
  }
`;

export const DropdownContainer = styled.div<IDropdownContainerProps>`
  position: absolute;
  height: ${({ selectClientRect }) => selectClientRect?.height || 0}px;
  width: ${({ selectClientRect }) => selectClientRect?.width || 0}px;
  left: ${({ selectClientRect }) => selectClientRect?.left || 0}px;
  top: ${({ selectClientRect }) => selectClientRect?.top || 0}px;

  > div {
    height: 100%;
    width: 100%;
    position: relative;
  }

  transition: visibility ease-in 0.2s;

  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
`;

export const Dropdown = styled.ul<IDropdownProps>`
  position: absolute;
  background-color: ${({ theme }) => theme.colors.surface};

  ${({ position, size }) =>
    position === 'bottom'
      ? css`
          top: 100%;
        `
      : css`
          bottom: ${getHeight[size]};
        `}

  width: 100%;
  left: 0;
  border: ${({ theme }) => `1px solid ${theme.colors.gray[100]}`};
  box-shadow: ${({ theme }) => theme.boxShadow.sm};
  overflow-x: auto;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  z-index: 1000;

  transition: visibility ease-in 0.2s, opacity ease-in 0.2s,
    transform ease-in 0.2s;

  transform: ${({ position }) =>
    position === 'bottom' ? 'translateY(-6px)' : 'translateY(6px)'};
  opacity: 0;
  visibility: hidden;

  max-height: 200px;

  ${({ isVisible, position }) =>
    isVisible &&
    css`
      opacity: 1;
      visibility: visible;
      transform: ${position === 'bottom'
        ? 'translateY(6px)'
        : 'translateY(-6px)'};
    `}
`;

export const Option = styled.li<IOptionProps>`
  display: flex;
  align-items: center;

  cursor: pointer;
  font-size: 12px;
  line-height: 20px;
  min-height: 36px;
  padding: ${({ theme, size }) =>
    `${theme.spacing.xs} ${size === 'lg' ? theme.spacing.sm : '12px'}`};
  gap: ${({ theme }) => theme.spacing.xs};

  ${({ level, theme, size }) =>
    level > 1 &&
    css`
      padding-left: ${`${
        (size === 'lg' ? Number(theme.spacing.sm.replace('px', '')) : 12) *
        (level - 1)
      }px`};
    `}

  background-color: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.onSurface};

  transition: background-color ease-in 0.3s, filter ease-in 0.2s,
    color ease-in 0.2s;

  &:hover {
    filter: brightness(0.97);
  }

  & + & {
    border-top: ${({ theme }) => `1px solid ${theme.colors.gray[100]}`};
  }

  ${({ isDisabled }) =>
    isDisabled &&
    css`
      cursor: not-allowed;
      pointer-events: none;
      opacity: 0.7;
    `}

  ${({ isPlaceholder, theme }) =>
    isPlaceholder &&
    css`
      font-weight: 600;
      color: ${theme.colors.gray[200]};
    `}

  ${({ isActive, theme }) =>
    isActive &&
    css`
      font-weight: 600;
      background: ${transparentize(0.96, theme.colors.primary)};
      color: ${theme.colors.primary};
    `}

  svg {
    width: 16px;
    height: 16px;
  }

  > div {
    display: flex;
    flex-direction: column;

    > p {
      color: ${({ theme }) => theme.colors.gray[300]};
    }
  }
`;

export const NoOptions = styled.span<INoOptionsProps>`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  font-size: 12px;
  line-height: 36px;
  height: 36px;
  padding: ${({ theme, size }) =>
    `0 ${size === 'lg' ? theme.spacing.sm : '12px'}`};
  text-align: center;

  color: ${({ theme }) => theme.colors.onSurface};
  background-color: ${({ theme }) => theme.colors.surface};
`;
